/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnInit, SimpleChanges, computed, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './audit-history.component.html';

import {ObjectTypeFinder} from '../../common/constants/object-type';
import {stringToCurrency} from '../../../components/util/iso-currency-format';
import {AdnMessageParamsService} from '../../common/filters/adn-message-params.service';
import {formatLocaleDate} from '../../common/locale/locale-date-format';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {AuditResource} from '../../../components/api/resources/resource/audit';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {interpolateTemplate} from '../../common/filters/interpolate-template';
import {reportMissingTranslateKey, summarizeTranslateMessage} from '../../common/filters/missing-translate-key';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';
import {StateService} from '@uirouter/core';

export interface AuditHistoryHook {
  objectType?: string;
  startDate?: any;
  endDate?: any;
  chartMode?: boolean;
  triggerAuditQuery?: () => void;
}

@Component({
  standalone: false,
  selector: 'adn-audit-history',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditHistoryComponent implements OnInit, OnChanges {
  @Input() object: any;
  @Input() hook: AuditHistoryHook;
  @Input() objectType: string;

  readonly history = signal<any[] | undefined>(undefined);
  readonly masquerade = signal(false);
  readonly currentPage = signal(1);
  readonly totalCount = signal(0);
  readonly pageQuery = signal<{ $resolved: boolean }>({ $resolved: false });
  readonly pageSize = 25;

  readonly historyResolved = computed(() => this.history() !== undefined);
  readonly totalPages = computed(() => Math.max(1, Math.ceil((this.totalCount() || 0) / this.pageSize)));
  readonly pageNumbers = computed(() => visiblePageNumbers(this.currentPage(), this.totalPages()));
  readonly pageRangeStart = computed(() => this.pageSize * (this.currentPage() - 1) + 1);
  readonly pageRangeEnd = computed(() => Math.min(this.pageSize * this.currentPage(), this.totalCount() || 0));

  constructor(
    private readonly translate: TranslateService,
    private readonly $state: StateService,
    private readonly auditResource: AuditResource,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnMessageParams: AdnMessageParamsService,
    private readonly appLocale: AppLocaleService
  ) {
  }

  ngOnInit(): void {
    this.bindHook();
    this.loadIfReady();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hook) {
      this.bindHook();
    }
    if (changes.object || changes.objectType) {
      const previousId = changes.object?.previousValue?.id;
      if (previousId && previousId !== this.object?.id) {
        this.currentPage.set(1);
      }
      this.loadIfReady();
    }
  }

  private bindHook(): void {
    if (this.hook) {
      this.hook.triggerAuditQuery = () => {
        this.auditQuery();
      };
    }
  }

  private loadIfReady(): void {
    if (!this.object?.id) {
      return;
    }
    this.auditQuery();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) {
      return;
    }
    this.currentPage.set(page);
    this.auditQuery();
  }

  private auditQuery(): void {
    if (!this.object?.id) {
      this.history.set([]);
      this.pageQuery.set({$resolved: true});
      return;
    }

    this.pageQuery.set({$resolved: false});
    this.auditResource.get(this.object, this.getObjectType(), {
      pageSize: this.pageSize,
      page: this.currentPage(),
      startDate: this.hook?.startDate,
      endDate: this.hook?.endDate,
      chartMode: this.hook?.chartMode
    }).then((page) => {
      let masquerade = false;
      const history = _.map(page.results, (item: any) => {
        if (!masquerade && item.masqueraderUser) {
          masquerade = true;
        }
        const messages = _.map(item.messages, (message: any) => {
          return this.processMessage(message);
        });
        return _.assign({}, item, {
          adjustedTime: item.timestamp,
          messages
        });
      });
      this.masquerade.set(masquerade);
      this.history.set(history);
      this.totalCount.set(page.totalCount || 0);
      this.pageQuery.set({$resolved: true});
    });
  }

  private getObjectType(): string | undefined {
    return this.objectType
      || _.get(this.hook, ['objectType'])
      || _.get(this.object, ['objectType', 'type'])
      || _.get(this.object, ['objectType'])
      || _.get(ObjectTypeFinder.findObjectType(this.object), ['type'])
      || _.get(ObjectTypeFinder.findObjectFromStateName(_.get(this.$state, ['current', 'name'])), ['type']);
  }

  private processMessage(message: any): any {
    const processed = this.adnMessageParams.transform(_.cloneDeep(message), {status: 'forecast.status'});
    const interpolateParams = this.formatMessageParams(_.clone(processed.parameters));
    // ngx-translate throws if key is null/undefined/'' (audit rows can omit code).
    if (processed.code) {
      processed.uiTranslation = this.translate.instant(processed.code, interpolateParams);
      if (processed.uiTranslation === processed.code) {
        processed.uiTranslation = undefined;
      }
    } else {
      reportMissingTranslateKey({
        source: 'AuditHistoryComponent.processMessage',
        key: processed.code,
        missingField: 'code',
        interpolateParams,
        messageSummary: summarizeTranslateMessage(processed)
      });
      processed.uiTranslation = undefined;
    }

    const newParams = _.clone(interpolateParams);
    const defaultCurrency = this.LocalNetworkProfile.get('defaultCurrency');
    _.forEach(newParams, (value, key) => {
      if (_.endsWith(key.toLowerCase(), 'id')) {
        delete newParams[key];
      } else if (key.toLowerCase().indexOf('name') > -1) {
        newParams[key] = '<strong>' + value + '</strong>';
      } else {
        newParams[key] = stringToCurrency(value, defaultCurrency, this.appLocale.getLocaleId());
      }
    });
    processed.apiTranslation = interpolateTemplate(this.stripLocaleDateFilters(processed.text))(newParams);
    return processed;
  }

  private formatMessageParams(params: any): any {
    if (!params) {
      return params;
    }
    const locale = this.appLocale.getLocaleId();
    const timeZone = this.LocalNetworkProfile.get('timeZone') || 'utc';
    _.forEach(['startDate', 'endDate'], (key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        params[key] = formatLocaleDate(params[key], 'short', locale, timeZone);
      }
    });
    return params;
  }

  private stripLocaleDateFilters(text: string): string {
    if (!text) {
      return text;
    }
    return String(text).replace(/\s*\|\s*localeDate\s*:\s*["'][^"']+["']/g, '');
  }
}
